import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';

import { 
  createBonliv, validateBonliv, getBonlivById, updateBonliv, deleteBonliv,
  getAllBonliv, getBonlivByClient, getBonlivByCommercial, 
  deleteItemFromBonliv, updateItemInBonliv, getItemsInBonliv, bulkUpdateItemsInBonliv, updateBonlivStatus, saveBonliv, unvalidateBonliv, getBonlivPerEtage, getAllBonlivPerEtageOverview, 
  getAvailableEtages, transferBonlivToGroupe, getAllBonlivForCollection,
  recreateBonlivPerEtageForAll, recreateBonlivPerEtageForOne,
  // Seed DBF upload
  uploadBonlivDBF,
  uploadBonlivDetailsDBF,
  uploadBothBonlivDBF
} from '../models/bonlivAPI'; // Adjust the import path as necessary

export const useCreateBonliv = () => {
  const [error, setError] = useState('');
  const [isCreated, setIsCreated] = useState(false);

  const handleCreate = async (bonlivData) => {
    try {
      await createBonliv(bonlivData);
      setIsCreated(true);
    } catch (err) {
      setError(err.message);
      setIsCreated(false);
    }
  };

  return { handleCreate, error, isCreated };
};

export const useValidateBonliv = (refBonliv) => {
  const [error, setError] = useState('');
  const [isValidated, setIsValidated] = useState(false);

  const validate = async () => {
    setError('');
    try {
      
      await validateBonliv(refBonliv);
      setIsValidated(true);
    } catch (err) {
      const reason = err.response?.data?.message || err.message;
      setError(reason);
      setIsValidated(false);
      throw new Error(reason);
    }
  };

  return { validate, error, isValidated };
};

// In bonlivHooks.js

export const useFetchBonlivById = (initialId) => {
  const [id, setId] = useState(initialId);
  const [bonliv, setBonliv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchBonliv = useCallback(() => {
    if (id) {
      setLoading(true);
      setError('');
      getBonlivById(id)
        .then(response => {
          setBonliv(response.data);
          setLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [id]);

  useEffect(() => {
    fetchBonliv();
  }, [fetchBonliv]);

  return { bonliv, loading, error, refetch: fetchBonliv, setId };
};

export const useFetchItemsInBonliv = (initialRefBL) => {
  const [refBL, setRefBL] = useState(initialRefBL);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchItems = useCallback(() => {
    if (refBL) {
      setLoading(true);
      setError('');
      console.log(`Fetching items for Bonliv: ${refBL}`);
      
      getItemsInBonliv(refBL)
        .then(response => {
         
          const fetchedItems = Array.isArray(response.data) ? response.data : response.data.Response || [];
          setItems(fetchedItems);
          
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching items:', err);
          setError(err.message || 'Unknown error');
          setLoading(false);
        });
    } else {
      console.log('No refBL provided, skipping fetch');
    }
  }, [refBL]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return { items, loading, error, refetch: fetchItems, setRefBL };
};


export const useUpdateBonliv = () => {
  const [error, setError] = useState('');
  const [isUpdated, setIsUpdated] = useState(false);

  const handleUpdate = async (id, bonlivData) => {
    try {
      await updateBonliv(id, bonlivData);
      setIsUpdated(true);
    } catch (err) {
      setError(err.message);
      setIsUpdated(false);
    }
  };

  return { handleUpdate, error, isUpdated };
};

export const useDeleteBonliv = () => {
  const [error, setError] = useState('');
  const [isDeleted, setIsDeleted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await deleteBonliv(id);
      setIsDeleted(true);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setIsDeleted(false);
    } finally {
      setLoading(false);
    }
  };

  return { handleDelete, error, isDeleted, loading };
};


export const useFetchAllBonliv = (params) => {
  const [bonliv, setBonliv] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchBonliv = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const { data } = await getAllBonliv(params);
      setBonliv(data.bonlivs);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(params)]); // Use JSON.stringify to create a stable dependency

  useEffect(() => {
    fetchBonliv();
  }, [fetchBonliv]);

  // Function to update parameters
  const updateParams = (newParams) => {
    // This will trigger a re-fetch due to the dependency change
    return fetchBonliv();
  };

  return {
    bonliv,
    total,
    loading,
    error,
    refetch: fetchBonliv,
    updateParams
  };
};




// Example for useFetchBonlivByClient
export const useFetchBonlivByClient = (clientId) => {
  const [bonliv, setBonliv] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (clientId) {
      setLoading(true);
      getBonlivByClient(clientId)
        .then(response => {
          setBonliv(response.data);
          setLoading(false);
        })
        .catch(err => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [clientId]);

  return { bonliv, loading, error };
};

export const useFetchBonlivByCommercial = (commercialId) => {
    const [bonliv, setBonliv] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
  
    useEffect(() => {
      if (commercialId) {
        setLoading(true);
        getBonlivByCommercial(commercialId)
          .then(response => {
            setBonliv(response.data);
            setLoading(false);
          })
          .catch(err => {
            setError(err.message);
            setLoading(false);
          });
      }
    }, [commercialId]);
  
    return { bonliv, loading, error };
  };

 

  
  export const useDeleteItemFromBonliv = () => {
    const [error, setError] = useState('');
    const [isDeleted, setIsDeleted] = useState(false);
  
    const deleteItem = async (refBonliv, codeArt) => {
      try {
        await deleteItemFromBonliv(refBonliv, codeArt);
        setIsDeleted(true);
      } catch (err) {
        setError(err.message);
        setIsDeleted(false);
      }
    };
  
    return { deleteItem, error, isDeleted };
  };

  
  export const useUpdateItemInBonliv = () => {
    const [error, setError] = useState('');
    const [isUpdated, setIsUpdated] = useState(false);
  
    const updateItem = async (refBonliv, codeArt, itemData) => {
      try {
        await updateItemInBonliv(refBonliv, codeArt, itemData);
        setIsUpdated(true);
      } catch (err) {
        setError(err.message);
        setIsUpdated(false);
      }
    };
  
    return { updateItem, error, isUpdated };
  };

  

  

  
  export const useBulkUpdateItemsInBonliv = () => {
    const [error, setError] = useState('');
    const [isUpdated, setIsUpdated] = useState(false);
  
    const bulkUpdateItems = async (updates) => {
      try {
        const response = await bulkUpdateItemsInBonliv(updates);
        setIsUpdated(true);
        return response.data; // You can return the updated items if your API does so
      } catch (err) {
        setError(err.message);
        setIsUpdated(false);
      }
    };
  
    return { bulkUpdateItems, error, isUpdated, setError, setIsUpdated };
  };


  export const useUpdateBonlivStatus = () => {
    const [error, setError] = useState('');
    const [isUpdated, setIsUpdated] = useState(false);
  
    const updateStatus = async (refBL, newStatus) => {
      setError('');
      try {
        const result = await updateBonlivStatus(refBL, newStatus);
        setIsUpdated(true);
        return result; // Return the updated bonliv
      } catch (err) {
        const reason = err.response?.data?.message || err.message;
        setError(reason);
        setIsUpdated(false);
        throw new Error(reason);
      }
    };
  
    return { updateStatus, error, isUpdated };
  };
  

  export const useSaveBonliv = () => {
    const [error, setError] = useState('');
    const [isSaved, setIsSaved] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
  
    const handleSaveBonliv = async (data) => {
      setIsLoading(true);
      setError('');
      try {
        await saveBonliv(data);
        setIsSaved(true);
        setError('');
      } catch (err) {
        setError(err.message || 'Erreur de sauvegarde');
        setIsSaved(false);
        throw err; // Re-throw to allow caller to handle
      } finally {
        setIsLoading(false);
      }
    };
  
    return { handleSaveBonliv, error, isSaved, isLoading };
  };

  export const useUnvalidateBonliv = () => {
    const [error, setError] = useState('');
    const [isUnvalidated, setIsUnvalidated] = useState(false);
  
    const unvalidate = async (id) => {
      try {
        await unvalidateBonliv(id);
        setIsUnvalidated(true);
      } catch (err) {
        setError(err.message);
        setIsUnvalidated(false);
      }
    };
  
    return { unvalidate, error, isUnvalidated };
  };

export const useFetchBonlivPerEtage = (initialRefBL) => {
  const [refBL, setRefBL] = useState(initialRefBL);
  const [etageData, setEtageData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchEtageData = useCallback(() => {
    if (refBL) {
      setLoading(true);
      setError('');
      console.log(`Fetching BonlivPerEtage for: ${refBL}`);
      
      getBonlivPerEtage(refBL)
        .then(response => {
          setEtageData(response.data || []); 
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching BonlivPerEtage:', err);
          setError(err.response?.data?.message || err.message || 'Unknown error');
          setLoading(false);
        });
    } else {
      console.log('No refBL provided for BonlivPerEtage fetch, skipping');
      setEtageData([]); // Clear data if refBL is null/undefined
    }
  }, [refBL]);

  useEffect(() => {
    fetchEtageData();
  }, [fetchEtageData]);

  return { etageData, loading, error, refetch: fetchEtageData, setRefBL };
};


export const useFetchAllBonlivPerEtageOverview = (initialParams = { page: 1, pageSize: 20 }) => {
  const [params, setParams] = useState(initialParams);
  const [overviewData, setOverviewData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableEtages, setAvailableEtages] = useState([]);

  // Fetch available etages on component mount
  useEffect(() => {
    const fetchEtages = async () => {
      try {
        const response = await getAvailableEtages();
        setAvailableEtages(response.data.etages || []);
      } catch (err) {
        console.error('Error fetching available etages:', err);
        // Don't set error state here to avoid blocking main content
      }
    };

    fetchEtages();
  }, []);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    console.log("Fetching BonlivPerEtage overview with params:", params);
    try {
      const response = await getAllBonlivPerEtageOverview(params);
      setOverviewData(response.data.overviewData || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching BonlivPerEtage overview:', err);
      setError(err.response?.data?.message || err.message || 'Unknown error');
      setOverviewData([]); // Clear data on error
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(params)]); // Use JSON.stringify for stable dependency

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]); // Re-run when params change

  // Function to update filter/pagination parameters
  const updateParams = (newParams) => {
    setParams(prevParams => ({ ...prevParams, ...newParams }));
  };

  return { 
    overviewData, 
    total, 
    loading, 
    error, 
    refetch: fetchOverview, 
    updateParams,
    availableEtages
  };
};

export const useTransferBonlivToGroupe = () => {
  const [error, setError] = useState('');
  const [isTransferred, setIsTransferred] = useState(false);
  const [loading, setLoading] = useState(false);

  const transfer = async (id) => {
    setLoading(true);
    setError('');
    try {
      const result = await transferBonlivToGroupe(id);
      setIsTransferred(true);
      setLoading(false);
      return result.data;
    } catch (err) {
      setError(err.message || 'Failed to transfer bonliv to groupe');
      setIsTransferred(false);
      setLoading(false);
      throw err;
    }
  };

  return { transfer, error, isTransferred, loading };
};

export const useFetchAllBonlivForCollection = ({ page = 1, pageSize = 20, filter = 'all', entrepot, clientSearch, refDevSearch }) => {
  const [data, setData] = useState({ bonlivs: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllBonlivForCollection({
        page,
        pageSize,
        filter,
        entrepot,
        clientSearch,
        refDevSearch
      });
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filter, entrepot, clientSearch, refDevSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    bonlivs: data.bonlivs,
    total: data.total,
    loading,
    error,
    refetch: fetchData
  };
};

/**
 * Hook to recreate BonlivPerEtage records for all BonLivs in EN_PREPARATION
 * @returns {Object} - { recreateAll, loading, error, result }
 */
export const useRecreateBonlivPerEtageForAll = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const recreateAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await recreateBonlivPerEtageForAll();
      setResult(response.data);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to recreate BonlivPerEtage';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  return { recreateAll, loading, error, result };
};

/**
 * Hook to recreate BonlivPerEtage records for a specific BonLiv
 * @returns {Object} - { recreateOne, loading, error, result }
 */
export const useRecreateBonlivPerEtageForOne = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const recreateOne = useCallback(async (identifier) => {
    setLoading(true);
    setError(null);
    try {
      const response = await recreateBonlivPerEtageForOne(identifier);
      setResult(response.data);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to recreate BonlivPerEtage';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  return { recreateOne, loading, error, result };
};

// Seed DBF upload hooks
export const useUploadBonlivDBF = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);

  const handleUpload = async (file) => {
    setLoading(true);
    setError('');
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await uploadBonlivDBF(formData);
      setUploadResult(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during upload';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { handleUpload, loading, error, uploadResult };
};

export const useUploadBonlivDetailsDBF = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);

  const handleUpload = async (file) => {
    setLoading(true);
    setError('');
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await uploadBonlivDetailsDBF(formData);
      setUploadResult(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during upload';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { handleUpload, loading, error, uploadResult };
};

export const useUploadBothBonlivDBF = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);

  const handleUpload = async (bonlivFile, detailsFile) => {
    setLoading(true);
    setError('');
    setUploadResult(null);

    const formData = new FormData();
    formData.append('bonlivFile', bonlivFile);
    formData.append('detailsFile', detailsFile);

    try {
      const response = await uploadBothBonlivDBF(formData);
      setUploadResult(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during upload';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { handleUpload, loading, error, uploadResult };
};
